import Highcharts from '~/scripts/integrations/highcharts';

class RecognitionROICalculator {
  constructor() {
    this.form = document.getElementById('calculator-form');
    this.employeeCountInput = document.getElementById('employee-count');
    this.engagementLevelInput = document.getElementById('engagement-level');
    this.managerRecognitionPercentageInput = document.getElementById('manager-recognition-percentage');
    this.managerRecognitionFrequencyInput = document.getElementById('manager-recognition-frequency');
    this.peerRecognitionPercentageInput = document.getElementById('peer-recognition-percentage');
    this.peerRecognitionFrequencyInput = document.getElementById('peer-recognition-frequency');
    this.programDurationInput = document.getElementById('program-duration');
    this.industryInput = document.getElementById('industry');
    this.roiAmount = document.getElementById('roi-amount');
    this.roiText = document.getElementById('roi-text');
    this.resetBtn = document.getElementById('reset-roi-calculator');

    // Chart instances
    this.roiTimelineChart = null;
    this.benefitsBreakdownChart = null;

    // Industry average salaries
    this.industrySalaries = {
      technology: 95000,
      healthcare: 65000,
      manufacturing: 55000,
      finance: 75000,
      retail: 35000,
      construction: 50000,
      education: 45000,
      other: 60000
    };

    this.bindEvents();
    this.calculateROI();
  }

  bindEvents() {
    [
      this.employeeCountInput,
      this.engagementLevelInput,
      this.managerRecognitionPercentageInput,
      this.managerRecognitionFrequencyInput,
      this.peerRecognitionPercentageInput,
      this.peerRecognitionFrequencyInput,
      this.programDurationInput,
      this.industryInput
    ].forEach(input => {
      input.addEventListener('input', () => this.calculateROI());
      input.addEventListener('change', () => this.calculateROI());
    });

    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', () => this.reset());
    }
  }

  formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  reset() {
    this.form.reset();
    this.calculateROI();
  }

  updateROITimelineChart(roiData, programDuration) {
    if (this.roiTimelineChart) {
      this.roiTimelineChart.destroy();
    }

    this.roiTimelineChart = Highcharts.chart('roi-timeline-chart', {
      chart: {
        type: 'spline',
        height: 300
      },
      title: {
        text: 'ROI Projection Over Time'
      },
      xAxis: {
        title: {
          text: 'Months'
        },
        min: 0,
        max: programDuration
      },
      yAxis: {
        title: {
          text: 'ROI (%)'
        },
        labels: {
          format: '{value}%'
        }
      },
      tooltip: {
        pointFormat: '<b>Month {point.x}</b><br/>' +
                    'ROI: {point.y:.1f}%'
      },
      plotOptions: {
        spline: {
          marker: {
            enabled: true
          }
        }
      },
      series: [{
        name: 'ROI',
        data: roiData,
        color: '#2D9EE2'
      }]
    });
  }

  updateBenefitsBreakdownChart(productivityGains, turnoverSavings, engagementBenefits, totalCosts) {
    const chartData = [
      {
        name: 'Productivity Gains',
        y: productivityGains,
        color: '#28a745'
      },
      {
        name: 'Turnover Savings',
        y: turnoverSavings,
        color: '#ffc107'
      },
      {
        name: 'Engagement Benefits',
        y: engagementBenefits,
        color: '#17a2b8'
      },
      {
        name: 'Program Costs',
        y: -totalCosts,
        color: '#dc3545'
      }
    ];

    if (this.benefitsBreakdownChart) {
      this.benefitsBreakdownChart.destroy();
    }

    this.benefitsBreakdownChart = Highcharts.chart('benefits-breakdown-chart', {
      chart: {
        type: 'column',
        height: 300
      },
      title: {
        text: 'Annual Benefits vs Costs'
      },
      xAxis: {
        type: 'category',
        labels: {
          rotation: -45,
          style: {
            fontSize: '11px'
          }
        }
      },
      yAxis: {
        title: {
          text: 'Amount ($)'
        },
        labels: {
          formatter: function() {
            return this.value >= 0 ? 
              '$' + (this.value / 1000000).toFixed(1) + 'M' : 
              '-$' + (Math.abs(this.value) / 1000000).toFixed(1) + 'M';
          }
        }
      },
      tooltip: {
        pointFormat: '<b>{point.name}</b><br/>' +
                    'Amount: {point.y:,.0f}'
      },
      plotOptions: {
        column: {
          dataLabels: {
            enabled: true,
            formatter: function() {
              return this.y >= 0 ? 
                '$' + (this.y / 1000000).toFixed(1) + 'M' : 
                '-$' + (Math.abs(this.y) / 1000000).toFixed(1) + 'M';
            }
          }
        }
      },
      series: [{
        name: 'Annual Impact',
        data: chartData
      }]
    });
  }

  calculateROI() {
    const employeeCount = parseFloat(this.employeeCountInput.value) || 0;
    const engagementLevel = parseFloat(this.engagementLevelInput.value) || 0.5;
    const managerRecognitionPercentage = parseFloat(this.managerRecognitionPercentageInput.value) || 0;
    const managerRecognitionFrequency = this.managerRecognitionFrequencyInput.value;
    const peerRecognitionPercentage = parseFloat(this.peerRecognitionPercentageInput.value) || 0;
    const peerRecognitionFrequency = this.peerRecognitionFrequencyInput.value;
    const programDuration = parseFloat(this.programDurationInput.value) || 24;
    const industry = this.industryInput.value;
    
    const avgSalary = this.industrySalaries[industry] || 60000;

    // Convert frequency to annual multiplier
    const getFrequencyMultiplier = (frequency) => {
      switch (frequency) {
        case 'weekly': return 52;
        case 'monthly': return 12;
        case 'quarterly': return 4;
        case 'yearly': return 1;
        default: return 12;
      }
    };

    // Calculate recognition counts based on percentages and frequencies
    const managerFrequencyMultiplier = getFrequencyMultiplier(managerRecognitionFrequency);
    const peerFrequencyMultiplier = getFrequencyMultiplier(peerRecognitionFrequency);
    
    const managerRecognitionsPerYear = (employeeCount * managerRecognitionPercentage / 100) * managerFrequencyMultiplier;
    const peerRecognitionsPerYear = (employeeCount * peerRecognitionPercentage / 100) * peerFrequencyMultiplier;
    
    const managerRecognitionsPerMonth = managerRecognitionsPerYear / 12;
    const peerRecognitionsPerMonth = peerRecognitionsPerYear / 12;
    
    // Calculate recognition impact
    const totalRecognitions = managerRecognitionsPerMonth + peerRecognitionsPerMonth;
    const recognitionPerEmployee = totalRecognitions / employeeCount;
    
    // Manager recognitions are 2x more impactful than peer recognitions
    const weightedRecognitions = (managerRecognitionsPerMonth * 2) + peerRecognitionsPerMonth;
    const weightedRecognitionPerEmployee = weightedRecognitions / employeeCount;

    // ROI factors based on research statistics
    const productivityIncrease = 0.14; // 14% increase in productivity when employees receive recognition frequently (Gallup)
    const highEngagementProductivity = 0.21; // 21% greater productivity for teams with high engagement (Gallup)
    const turnoverReduction = 0.31; // 31% lower voluntary turnover for companies with effective recognition programs (Bersin by Deloitte)
    const satisfactionImprovement = 0.69; // 69% of employees would work harder if better recognized (OfficeTeam)
    const engagementImprovement = 0.41; // 41% increase in employee engagement for organizations with recognition programs (SHRM)
    
    // Calculate engagement improvement potential
    const engagementGap = 0.85 - engagementLevel; // Target 85% engagement
    const engagementImprovementPotential = Math.min(engagementGap, engagementImprovement);

    // Calculate financial impact
    const annualPayroll = employeeCount * avgSalary;
    
    // Check if there are any recognitions at all
    const hasRecognitions = managerRecognitionPercentage > 0 || peerRecognitionPercentage > 0;
    
    // Productivity gains - based on research statistics
    // 14% increase for employees receiving frequent recognition
    // 21% increase for high engagement teams
    const recognitionFrequency = hasRecognitions ? Math.min(weightedRecognitionPerEmployee / 12, 1) : 0; // Zero if no recognitions
    const productivityMultiplier = 1 + (productivityIncrease * recognitionFrequency);
    const highEngagementMultiplier = 1 + (highEngagementProductivity * engagementImprovementPotential * recognitionFrequency); // Only apply if there are recognitions
    const combinedProductivityGain = (productivityMultiplier * highEngagementMultiplier - 1);
    const productivityGains = annualPayroll * combinedProductivityGain * 0.6; // Apply to 60% of workforce
    
    // Turnover cost savings - based on 31% reduction in voluntary turnover
    const turnoverCostPerEmployee = avgSalary * 0.20; // 20% of salary to replace employee
    const annualTurnover = employeeCount * 0.15; // 15% annual turnover rate (industry average)
    const turnoverSavings = annualTurnover * turnoverCostPerEmployee * turnoverReduction * recognitionFrequency;
    
    // Engagement and satisfaction benefits - based on research
    // 69% of employees would work harder if better recognized
    // 41% increase in employee engagement
    const satisfactionMultiplier = 1 + (satisfactionImprovement * recognitionFrequency * 0.3); // 30% of employees affected
    const engagementMultiplier = 1 + (engagementImprovementPotential * recognitionFrequency * 0.4); // Only apply if there are recognitions
    const combinedEngagementGain = (satisfactionMultiplier * engagementMultiplier - 1);
    const engagementBenefits = annualPayroll * combinedEngagementGain * 0.5; // Apply to 50% of workforce
    
    // More realistic program costs
    const programCosts = employeeCount * 150; // $150 per employee per year (was $50)
    
    // Recognition costs - more realistic pricing
    const recognitionCosts = (managerRecognitionsPerYear + peerRecognitionsPerYear) * 2; // Annual * $2 per recognition
    
    const totalBenefits = productivityGains + turnoverSavings + engagementBenefits;
    const totalCosts = programCosts + recognitionCosts;
    const netBenefits = totalBenefits - totalCosts;
    
    // Calculate ROI percentage
    const roiPercentage = totalCosts > 0 ? (netBenefits / totalCosts) * 100 : 0;
    const annualROI = roiPercentage / (programDuration / 12);

    // Generate ROI timeline data
    const roiTimelineData = [];
    for (let month = 0; month <= programDuration; month++) {
      // ROI starts at 0 and gradually increases, with diminishing returns
      const monthROI = annualROI * (month / 12) * (1 - Math.exp(-month / 6));
      roiTimelineData.push([month, monthROI]);
    }

    // Get the final ROI value from the timeline (should match the graph)
    const finalROI = roiTimelineData.length > 0 ? roiTimelineData[roiTimelineData.length - 1][1] : 0;
    
    // Update display - show the final ROI that matches the graph
    this.roiAmount.textContent = `${finalROI.toFixed(1)}%`;
    this.roiText.innerHTML = `
      Based on ${employeeCount.toLocaleString()} employees with ${(engagementLevel * 100).toFixed(0)}% current engagement.
      <br><small class="text-muted">
        Annual benefits: ${this.formatCurrency(totalBenefits)} | 
        Annual costs: ${this.formatCurrency(totalCosts)} | 
        Net annual benefit: ${this.formatCurrency(netBenefits)}
      </small>
      <br><small class="text-muted">
        <strong>Research-based factors:</strong> 14% productivity increase (Gallup), 31% turnover reduction (Bersin), 41% engagement boost (SHRM)
      </small>
    `;

    // Update charts
    this.updateROITimelineChart(roiTimelineData, programDuration);
    this.updateBenefitsBreakdownChart(productivityGains, turnoverSavings, engagementBenefits, totalCosts);
  }
}

export default RecognitionROICalculator;
